import type { Questionnaire, QuestionnaireItem, QuestionnaireResponseItem } from "fhir/r3"
import { createContext, useMemo, useState, type ReactNode } from "react"
import { useParams } from "react-router-dom"
import { PageFunction } from "../types"
import { questionIsAnswered } from "../fhirUtils/questionnaireUtils"

type QuestionnaireErrorMap = Record<string, Record<"required", boolean>>

export interface QuestionnaireContext {
  pageFunction?: PageFunction
  pages: number
  step: number | null
  questions: QuestionnaireItem[]
  startQuestionnaire: (questionnaire: Questionnaire) => void
  responses: QuestionnaireResponseItem[]
  setResponses: (responses: QuestionnaireResponseItem[]) => void
  errors: QuestionnaireErrorMap
  validateResponses: () => boolean
}

//eslint-disable-next-line react-refresh/only-export-components
export const QuestionnaireContext = createContext<QuestionnaireContext | null>(null)

export const QuestionnaireProvider = ({
  children,
  initialQuestions = [],
  initialResponses = [],
}: {
  children: ReactNode
  initialResponses?: QuestionnaireResponseItem[]
  initialQuestions?: QuestionnaireItem[]
}): JSX.Element => {
  const [pages, setPages] = useState(1)

  const params = useParams()
  const pageId = params.pageId || null
  const step = pageId ? parseInt(pageId) : null

  const [questions, setQuestions] = useState<QuestionnaireItem[]>(initialQuestions)
  const [responses, setResponses] = useState<QuestionnaireResponseItem[]>(initialResponses)
  const [errors, setErrors] = useState<QuestionnaireErrorMap>({})

  const startQuestionnaire = (questionnaire: Questionnaire) => {
    const questions = questionnaire.item ?? []
    setQuestions(questions)
    setPages(questions.length)
    setResponses([])
    setErrors({})
  }

  const validateResponses = (): boolean => {
    const errorMap: QuestionnaireErrorMap = {}
    questions.forEach((q) => {
      if (q.type === "group") {
        q.item?.forEach((gq) => {
          const isAnswered = questionIsAnswered(gq, responses)
          if (!isAnswered && gq.required) {
            errorMap[gq.linkId] = { required: true }
          }
        })
      } else {
        const isAnswered = questionIsAnswered(q, responses)
        if (!isAnswered && q.required) {
          errorMap[q.linkId] = { required: true }
        }
      }
    })
    setErrors(errorMap)
    return !Object.keys(errorMap).length
  }

  const pageFunction: PageFunction = useMemo(
    () => (params.patientAction === "reschedule" ? "reschedule" : "cancel"),
    [params.patientAction],
  )

  return (
    <QuestionnaireContext.Provider
      value={{
        pages,
        step,
        questions,
        startQuestionnaire,
        responses,
        setResponses,
        pageFunction,
        errors,
        validateResponses,
      }}
    >
      {children}
    </QuestionnaireContext.Provider>
  )
}
